import React from "react";
import { Loader2 } from "lucide-react";
import { uploadImage, fileUrl } from "@/lib/api";

export function PageHeader({ title, subtitle, actions, icon: Icon }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
      <div className="flex items-center gap-3 min-w-0">
        {Icon && (
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Icon size={22} />
          </div>
        )}
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-slate-800 truncate">{title}</h1>
          {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function Card({ children, className = "", ...rest }) {
  return (
    <div
      className={`bg-white rounded-2xl border border-slate-200/80 shadow-sm ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}

export function StatCard({ label, value, sub, icon: Icon, tone = "default", testid }) {
  const tones = {
    default: "text-slate-800",
    primary: "text-primary",
    danger: "text-rose-600",
    warning: "text-amber-600",
    success: "text-emerald-600",
  };
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-500">{label}</span>
        {Icon && <Icon size={18} className="text-slate-400" />}
      </div>
      <div className={`text-2xl font-bold mt-2 ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </Card>
  );
}

export function Loading({ label = "Memuat..." }) {
  return (
    <div className="flex items-center justify-center py-20 text-slate-400 gap-2" data-testid="loading">
      <Loader2 className="animate-spin" size={20} />
      <span>{label}</span>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, message, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center px-6" data-testid="empty-state">
      {Icon && (
        <div className="w-14 h-14 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center mb-4">
          <Icon size={26} />
        </div>
      )}
      <h3 className="font-semibold text-slate-700">{title}</h3>
      {message && <p className="text-sm text-slate-400 mt-1 max-w-sm">{message}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Badge({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${className}`}>
      {children}
    </span>
  );
}

export function Button({ children, variant = "primary", size = "md", className = "", ...rest }) {
  const variants = {
    primary: "bg-primary text-white hover:bg-primary/90",
    outline: "border border-slate-300 text-slate-700 hover:bg-slate-50",
    ghost: "text-slate-600 hover:bg-slate-100",
    danger: "bg-rose-600 text-white hover:bg-rose-700",
    subtle: "bg-primary/10 text-primary hover:bg-primary/20",
  };
  const sizes = { sm: "h-8 px-3 text-sm", md: "h-10 px-4 text-sm", lg: "h-11 px-5 text-base" };
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 rounded-xl font-medium transition-colors disabled:opacity-50 disabled:pointer-events-none ${variants[variant]} ${sizes[size]} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

export function Input({ label, className = "", ...rest }) {
  return (
    <label className="block">
      {label && <span className="text-sm font-medium text-slate-600 mb-1.5 block">{label}</span>}
      <input
        className={`w-full h-10 px-3 rounded-xl border border-slate-300 bg-white text-slate-800 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition ${className}`}
        {...rest}
      />
    </label>
  );
}

export function Select({ label, children, className = "", ...rest }) {
  return (
    <label className="block">
      {label && <span className="text-sm font-medium text-slate-600 mb-1.5 block">{label}</span>}
      <select
        className={`w-full h-10 px-3 rounded-xl border border-slate-300 bg-white text-slate-800 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition ${className}`}
        {...rest}
      >
        {children}
      </select>
    </label>
  );
}

export function PhotoUpload({ value, onChange, label = "Foto", tall = false, testid = "photo-upload" }) {
  const [busy, setBusy] = React.useState(false);
  const inputRef = React.useRef(null);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const res = await uploadImage(file);
      onChange(res.id);
    } catch (err) {
      alert(err.message || "Gagal mengunggah foto");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return (
    <div>
      <span className="text-sm font-medium text-slate-600 mb-1 block">{label}</span>
      <div className="flex items-center gap-3">
        <div className={`w-20 ${tall ? "h-28" : "h-20"} rounded-xl bg-slate-100 overflow-hidden flex items-center justify-center shrink-0 border border-slate-200`}>
          {value ? <img src={fileUrl(value)} alt="" className="w-full h-full object-cover" /> : <span className="text-2xl">💐</span>}
        </div>
        <div className="flex flex-col gap-2 items-start">
          <input ref={inputRef} type="file" accept="image/*" onChange={pick} className="hidden" data-testid={`${testid}-input`} />
          <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="inline-flex items-center gap-2 px-3 h-9 rounded-xl border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50" data-testid={testid}>
            {busy ? "Mengunggah..." : value ? "Ganti Foto" : "Unggah Foto"}
          </button>
          {value && <button type="button" onClick={() => onChange(null)} className="text-xs text-rose-600" data-testid={`${testid}-remove`}>Hapus foto</button>}
        </div>
      </div>
    </div>
  );
}

export function Modal({ open, onClose, title, children, maxWidth = "max-w-lg" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className={`bg-white rounded-2xl shadow-xl w-full ${maxWidth} max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="px-6 py-4 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl">
            <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
          </div>
        )}
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
